import styled, { keyframes } from 'styled-components';

export const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

export const MiniMap = styled.div<React.HTMLAttributes<HTMLDivElement>>`
  position: fixed;
  top: 160px;
  right: 20px;
  z-index: 999;
  min-width: 300px;
  min-height: 300px;
  box-sizing: border-box;
  background: linear-gradient(135deg, rgb(255 255 255 / 95%) 0%, rgb(255 255 255 / 90%) 100%);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgb(255 255 255 / 30%);
  box-shadow: 0 8px 32px rgb(0 0 0 / 12%);
  animation: ${fadeIn} 0.3s ease-out;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgb(0 0 0 / 15%);
  }
`;

export const Header = styled.div<React.HTMLAttributes<HTMLDivElement>>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  color: rgb(0 0 0 / 65%);
  font-weight: 600;
  font-size: 14px;
`;

export const IconWrapper = styled.div<React.HTMLAttributes<HTMLDivElement>>`
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgb(255 255 255 / 20%);
    animation: ${pulse} 0.6s ease;
  }
`;

export const StepList = styled.div<React.HTMLAttributes<HTMLDivElement>>`
  padding: 12px 16px;
  max-height: 120px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgb(0 0 0 / 5%);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgb(0 0 0 / 20%);
    border-radius: 2px;
  }
`;

export const StepItem = styled.div<React.HTMLAttributes<HTMLDivElement>>`
  display: flex;
  align-items: center;
  padding: 6px 0;
  font-size: 12px;
  color: rgb(0 0 0 / 75%);
  border-bottom: 1px solid rgb(0 0 0 / 6%);

  &:last-child {
    border-bottom: none;
  }
`;

export const StepNumber = styled.div<React.HTMLAttributes<HTMLDivElement>>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  flex-shrink: 0;
`;

export const StepText = styled.div<React.HTMLAttributes<HTMLDivElement>>`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ChartContainer = styled.div<React.HTMLAttributes<HTMLDivElement>>`
  padding: 0 16px;
  flex: 1;
  background: rgb(255 255 255 / 50%);
  min-height: 160px;
`;

export const ViewDetailsButton = styled.div<React.HTMLAttributes<HTMLDivElement>>`
  padding: 12px 16px;
  text-align: center;
  color: rgb(0 0 0 / 65%);
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-top: 1px solid var(--Stroke-BorderSolidBase, #d9d9d9);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  &:hover {
    background: rgb(0 0 0 / 6%);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const StatsContainer = styled.div<React.HTMLAttributes<HTMLDivElement>>`
  display: flex;
  justify-content: left;
  padding: 8px 16px;
  background: rgb(255 255 255 / 30%);
  border-top: 1px solid rgb(0 0 0 / 6%);
`;

export const StatItem = styled.div<React.HTMLAttributes<HTMLDivElement>>`
  text-align: center;
  font-size: 11px;
  color: rgb(0 0 0 / 65%);
  margin-right: 16px;
`;

export const StatValue = styled.div<React.HTMLAttributes<HTMLDivElement>>`
  font-weight: 500;
  font-size: 14px;
  color: #000;
`;

export const StatLabel = styled.div<React.HTMLAttributes<HTMLDivElement>>`
  color: rgb(0 0 0 / 45%);
  margin-bottom: 2px;
`;

export const MinimizedContainer = styled.div<React.HTMLAttributes<HTMLDivElement>>`
  position: fixed;
  top: 160px;
  right: 20px;
  z-index: 999;
  width: 140px;
  min-height: 95px;
  background: linear-gradient(135deg, rgb(255 255 255 / 95%) 0%, rgb(255 255 255 / 90%) 100%);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgb(255 255 255 / 30%);
  box-shadow: 0 4px 20px rgb(0 0 0 / 12%);
  animation: ${fadeIn} 0.3s ease-out;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  cursor: pointer;
  padding: 8px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgb(0 0 0 / 15%);
    background: linear-gradient(135deg, rgb(103 126 234 / 10%) 0%, rgb(118 75 162 / 10%) 100%);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 4px;
    right: 6px;
    width: 0;
    height: 0;
    border-left: 3px solid transparent;
    border-right: 3px solid transparent;
    border-top: 3px solid rgb(102 126 234 / 50%);
    opacity: 0.6;
  }
`;

export const MinimizedHeader = styled.div<React.HTMLAttributes<HTMLDivElement>>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-bottom: 8px;
  color: #667eea;
  font-size: 12px;
  font-weight: 600;
`;

export const MinimizedIcon = styled.div<React.HTMLAttributes<HTMLDivElement>>`
  color: #667eea;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const MinimizedStats = styled.div<React.HTMLAttributes<HTMLDivElement>>`
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 10px;
`;

export const MinimizedStatItem = styled.div<React.HTMLAttributes<HTMLDivElement>>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: rgb(0 0 0 / 65%);
  padding: 1px 0;
`;

export const MinimizedStatValue = styled.span<React.HTMLAttributes<HTMLSpanElement>>`
  font-weight: 600;
  color: #667eea;
  font-size: 11px;
`;

export const MinimizedExpandHint = styled.div<React.HTMLAttributes<HTMLDivElement>>`
  position: absolute;
  bottom: 2px;
  left: 8px;
  font-size: 8px;
  color: rgb(0 0 0 / 40%);
  font-style: italic;
`;
